import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { apiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, ArrowRight, ArrowClockwise, Stop, Play, ArrowsClockwise, DownloadSimple, Globe, HardDrives, ShieldCheck, Database, Clock, CircleNotch, Trash, CloudCheck, CheckCircle, Warning, ArrowUUpLeft } from "@phosphor-icons/react";
import { toast } from "sonner";

function Metric({ label, value, sub, Icon }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{label}</p>
        {Icon && <Icon size={18} className="text-muted-foreground" weight="duotone" />}
      </div>
      <p className="mt-2 font-display text-2xl font-bold">{value}</p>
      {sub && <p className="mt-0.5 text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

function Bar({ label, pct }) {
  return (
    <div>
      <div className="mb-1.5 flex justify-between text-sm"><span className="text-muted-foreground">{label}</span><span className="font-medium">{pct}%</span></div>
      <div className="h-2 overflow-hidden rounded-full bg-secondary">
        <motion.div className="h-full rounded-full bg-primary" initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.8 }} />
      </div>
    </div>
  );
}

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [usage, setUsage] = useState(null);
  const [backups, setBackups] = useState([]);
  const [healthData, setHealthData] = useState(null);
  const [logs, setLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);
  const [busy, setBusy] = useState(false);
  const [domain, setDomain] = useState("");

  const load = () => api.get(`/projects/${id}`).then((r) => setProject(r.data));

  useEffect(() => {
    load().catch((e) => { toast.error(apiError(e)); navigate("/dashboard"); });
  }, [id]); // eslint-disable-line

  useEffect(() => {
    if (project?.deployment && project.deployment.status !== "failed") {
      api.get(`/projects/${id}/usage`).then((r) => setUsage(r.data)).catch(() => {});
      api.get(`/projects/${id}/backups`).then((r) => setBackups(r.data)).catch(() => {});
      api.get(`/projects/${id}/health`).then((r) => setHealthData(r.data)).catch(() => {});
      api.get(`/projects/${id}/logs`).then((r) => setLogs(r.data)).catch(() => {});
    }
  }, [project?.deployment, id]); // eslint-disable-line

  const action = async (act, label) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${id}/action/${act}`);
      setProject(data);
      toast.success(label);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const redeploy = async () => {
    setBusy(true);
    try {
      const plan = project.deployment?.plan || "starter";
      const { data } = await api.post(`/projects/${id}/deploy`, { plan });
      setProject(data);
      toast.success("Redeployed successfully.");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const deleteDeployment = async () => {
    try {
      const { data } = await api.delete(`/projects/${id}/deployment`);
      toast.success(data.message);
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const backup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${id}/backups`);
      toast.success(data.message);
      const r = await api.get(`/projects/${id}/backups`); setBackups(r.data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const restore = async (backupId) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${id}/backups/${backupId}/restore`);
      toast.success(data.message);
      const r = await api.get(`/projects/${id}/backups`); setBackups(r.data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const downloadBackup = async (backupId) => {
    try {
      const res = await api.get(`/projects/${id}/backups/${backupId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${backupId}.gz`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const connectDomain = async () => {
    try {
      const { data } = await api.post(`/projects/${id}/domain`, { domain });
      toast.success(data.message);
      await load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const download = async () => {
    try {
      const res = await api.get(`/projects/${id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${project.slug}-independent.zip`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async () => {
    try { await api.delete(`/projects/${id}`); toast.success("App deleted"); navigate("/dashboard"); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!project) return <Layout><div className="grid min-h-[60vh] place-items-center"><CircleNotch size={32} className="animate-spin text-muted-foreground" /></div></Layout>;

  const dep = project.deployment;
  const report = project.migration_report || {};
  const stopped = project.status === "stopped";

  return (
    <Layout>
      <div className="mx-auto max-w-5xl px-6 py-10 sm:px-10">
        <button onClick={() => navigate("/dashboard")} className="mb-6 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="back-button">
          <ArrowLeft size={16} /> Dashboard
        </button>

        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-display text-4xl font-bold tracking-tight">{project.name}</h1>
              <StatusBadge status={project.status} />
            </div>
            {dep && <p className="mt-2 flex items-center gap-1.5 font-mono text-sm text-muted-foreground"><Globe size={14} /> {dep.domain}</p>}
          </div>
          {!dep && project.status !== "live" && (
            <Button onClick={() => navigate(`/projects/${id}/flow`)} className="rounded-full active:scale-95" data-testid="continue-flow-button">
              Continue setup <ArrowRight size={16} weight="bold" />
            </Button>
          )}
        </div>

        {dep?.simulation && dep.status !== "failed" && (
          <div className="mt-6 rounded-xl bg-warning/40 px-4 py-3 text-sm text-warning-foreground" data-testid="simulation-banner">
            Simulation mode — hosting infrastructure is emulated in this environment. Your database, backups and health checks are real; the public web address is simulated until a real provider is connected.
          </div>
        )}
        {dep?.status === "failed" && (
          <div className="mt-6 rounded-xl bg-warning/40 px-4 py-3 text-sm text-warning-foreground" data-testid="failed-banner">
            <p className="font-semibold">{dep.error || "Deployment failed."}</p>
            <div className="mt-2 flex items-center gap-3">
              <Button size="sm" className="rounded-full" onClick={() => navigate(`/projects/${id}/flow`)} data-testid="retry-from-detail">Try again</Button>
              {dep.technical && <span className="font-mono text-xs opacity-80">{dep.technical}</span>}
            </div>
          </div>
        )}

        <Tabs defaultValue="overview" className="mt-8">
          <TabsList className="rounded-full">
            <TabsTrigger value="overview" data-testid="tab-overview" className="rounded-full">Overview</TabsTrigger>
            <TabsTrigger value="report" data-testid="tab-report" className="rounded-full">Migration report</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6">
            {dep && dep.status !== "failed" ? (
              <>
                <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                  <Metric label="Status" value={stopped ? "Offline" : "Online"} sub={stopped ? "App is stopped" : "Serving traffic"} Icon={CloudCheck} />
                  <Metric label="Uptime" value={`${dep.uptime_pct}%`} sub="Last 30 days" Icon={Clock} />
                  <Metric label="Database" value={healthData ? (healthData.database_ok ? "Healthy" : "Offline") : "Checking…"}
                    sub={healthData ? `${healthData.database?.storage_mb ?? 0} MB` : dep.database?.kind} Icon={Database} />
                  <Metric label="SSL" value="Secure" sub="Auto-renewing" Icon={ShieldCheck} />
                </div>

                <div className="mt-4 grid gap-4 lg:grid-cols-3">
                  <div className="rounded-2xl border border-border bg-card p-6 lg:col-span-2">
                    <p className="mb-4 text-sm font-semibold">Resource usage</p>
                    {usage ? (
                      <div className="space-y-4">
                        <Bar label="CPU" pct={usage.usage.cpu_pct} />
                        <Bar label="Memory" pct={usage.usage.ram_pct} />
                        <div className="grid grid-cols-3 gap-4 pt-2 text-center">
                          <div><p className="font-display text-xl font-bold">{usage.usage.storage_mb ?? 0} MB</p><p className="text-xs text-muted-foreground">Storage</p></div>
                          <div><p className="font-display text-xl font-bold">{usage.usage.collections ?? 0}</p><p className="text-xs text-muted-foreground">Collections</p></div>
                          <div><p className="font-display text-xl font-bold">{(usage.usage.objects ?? 0).toLocaleString()}</p><p className="text-xs text-muted-foreground">Records</p></div>
                        </div>
                      </div>
                    ) : <div className="grid h-32 place-items-center"><CircleNotch size={24} className="animate-spin text-muted-foreground" /></div>}
                  </div>

                  <div className="rounded-2xl border border-border bg-card p-6">
                    <div className="mb-4 flex items-center justify-between">
                      <p className="text-sm font-semibold">Backups</p>
                      <Button size="sm" variant="outline" className="rounded-full" onClick={backup} disabled={busy} data-testid="backup-button">
                        <ArrowUUpLeft size={14} /> Back up now
                      </Button>
                    </div>
                    {backups.length ? (
                      <ul className="space-y-2">
                        {backups.slice(0, 4).map((b) => (
                          <li key={b.id} className="rounded-xl bg-secondary px-3 py-2 text-xs" data-testid={`backup-${b.backup_id}`}>
                            <div className="flex items-center justify-between">
                              <span className="flex items-center gap-1.5"><CheckCircle size={14} weight="fill" className="text-success-foreground" /> {new Date(b.created_at).toLocaleString()}{b.safety ? " (safety)" : ""}</span>
                              <span className="text-muted-foreground">{b.size_mb} MB</span>
                            </div>
                            <div className="mt-1.5 flex gap-3">
                              <button onClick={() => downloadBackup(b.backup_id)} className="text-muted-foreground underline-offset-2 hover:text-foreground hover:underline" data-testid={`download-backup-${b.backup_id}`}>Download</button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild><button className="text-muted-foreground underline-offset-2 hover:text-foreground hover:underline" data-testid={`restore-backup-${b.backup_id}`}>Restore</button></AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader><AlertDialogTitle>Restore this backup?</AlertDialogTitle>
                                    <AlertDialogDescription>This replaces your app's current data with this backup. We'll take a safety snapshot first, but this can't be casually undone.</AlertDialogDescription></AlertDialogHeader>
                                  <AlertDialogFooter><AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => restore(b.backup_id)} className="rounded-full" data-testid={`confirm-restore-${b.backup_id}`}>Restore</AlertDialogAction></AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : <p className="text-sm text-muted-foreground">Daily backups enabled. No backups yet.</p>}
                  </div>
                </div>

                {logs.length > 0 && (
                  <div className="mt-4 rounded-2xl border border-border bg-card p-6">
                    <button onClick={() => setShowLogs((s) => !s)} className="flex w-full items-center justify-between text-sm font-semibold" data-testid="toggle-logs">
                      Advanced → Technical logs
                      <span className="text-xs font-normal text-muted-foreground">{showLogs ? "Hide" : "Show"}</span>
                    </button>
                    {showLogs && (
                      <ul className="mt-4 space-y-1.5 font-mono text-xs">
                        {logs.map((l, i) => (
                          <li key={i} className={l.level === "error" ? "text-destructive" : "text-muted-foreground"}>
                            <span className="opacity-60">{new Date(l.created_at).toLocaleTimeString()}</span> — {l.event}{l.technical ? ` · ${l.technical}` : ""}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}

                <div className="mt-4 rounded-2xl border border-border bg-card p-6">
                  <p className="mb-4 text-sm font-semibold">Quick actions</p>
                  <div className="flex flex-wrap gap-3">
                    <Button asChild className="rounded-full active:scale-95" data-testid="open-website-button">
                      <a href={dep.url} target="_blank" rel="noreferrer"><Globe size={16} /> Open website</a>
                    </Button>
                    {stopped ? (
                      <Button variant="outline" className="rounded-full" onClick={() => action("start", "App started")} disabled={busy} data-testid="start-button"><Play size={16} /> Start</Button>
                    ) : (
                      <Button variant="outline" className="rounded-full" onClick={() => action("stop", "App stopped")} disabled={busy} data-testid="stop-button"><Stop size={16} /> Stop</Button>
                    )}
                    <Button variant="outline" className="rounded-full" onClick={() => action("restart", "App restarted")} disabled={busy} data-testid="restart-button"><ArrowClockwise size={16} /> Restart</Button>
                    <Button variant="outline" className="rounded-full" onClick={redeploy} disabled={busy} data-testid="redeploy-button"><ArrowsClockwise size={16} /> Redeploy</Button>

                    <Dialog>
                      <DialogTrigger asChild><Button variant="outline" className="rounded-full" data-testid="domain-button"><Globe size={16} /> Domain</Button></DialogTrigger>
                      <DialogContent>
                        <DialogHeader><DialogTitle>Connect a custom domain</DialogTitle></DialogHeader>
                        <div className="space-y-2">
                          <Label htmlFor="domain">Your domain</Label>
                          <Input id="domain" placeholder="mybusiness.com" value={domain} onChange={(e) => setDomain(e.target.value)} data-testid="domain-input" className="rounded-xl" />
                          <p className="text-xs text-muted-foreground">We'll issue SSL and connect it automatically once your DNS points to us.</p>
                        </div>
                        <DialogFooter><Button onClick={connectDomain} className="rounded-full" data-testid="connect-domain-button">Connect domain</Button></DialogFooter>
                      </DialogContent>
                    </Dialog>

                    <Button variant="outline" className="rounded-full" onClick={download} data-testid="download-button"><DownloadSimple size={16} /> Download</Button>

                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button variant="ghost" className="rounded-full text-muted-foreground hover:text-foreground" data-testid="delete-deployment-button"><Stop size={16} /> Remove hosting</Button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Remove hosting only?</AlertDialogTitle>
                          <AlertDialogDescription>This takes the app offline and frees its containers, but keeps your database and backups so you can redeploy later.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={deleteDeployment} className="rounded-full" data-testid="confirm-delete-deployment">Remove hosting</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>

                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button variant="ghost" className="rounded-full text-destructive hover:bg-destructive/10 hover:text-destructive" data-testid="delete-button"><Trash size={16} /> Delete everything</Button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Delete this app and all its data?</AlertDialogTitle>
                          <AlertDialogDescription>This permanently removes the app, its database and all backups. This can't be undone.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={remove} className="rounded-full bg-destructive hover:bg-destructive/90" data-testid="confirm-delete">Delete everything</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </>
            ) : (
              <div className="rounded-3xl border border-dashed border-border bg-card p-12 text-center">
                <HardDrives size={36} weight="duotone" className="mx-auto text-muted-foreground" />
                <h3 className="mt-4 font-display text-xl font-medium">Not deployed yet</h3>
                <p className="mt-2 text-sm text-muted-foreground">Finish preparing and testing, then deploy to go live.</p>
                <Button onClick={() => navigate(`/projects/${id}/flow`)} className="mt-5 rounded-full active:scale-95" data-testid="go-to-flow">Continue setup</Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="report" className="mt-6">
            <div className="grid gap-4 md:grid-cols-3">
              <div className="rounded-2xl border border-border bg-card p-6 text-center">
                <p className="font-display text-4xl font-bold">{project.compatibility?.score ?? "—"}%</p>
                <p className="mt-1 text-sm text-muted-foreground">Compatibility</p>
                <StatusBadge status={project.status} className="mt-3" />
              </div>
              <div className="rounded-2xl border border-border bg-card p-6 md:col-span-2">
                <p className="text-sm font-semibold">What we detected</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {(report.summary?.detected || project.compatibility?.reasons || []).map((d, i) => (
                    <span key={i} className="rounded-full bg-secondary px-3 py-1 text-xs font-medium">{d}</span>
                  ))}
                </div>
                <div className="mt-4 grid grid-cols-3 gap-3 text-center">
                  <div><p className="font-display text-2xl font-bold">{report.summary?.changed ?? 0}</p><p className="text-xs text-muted-foreground">Files changed</p></div>
                  <div><p className="font-display text-2xl font-bold">{report.summary?.replaced ?? 0}</p><p className="text-xs text-muted-foreground">Settings replaced</p></div>
                  <div><p className="font-display text-2xl font-bold">{(report.summary?.generated || []).length}</p><p className="text-xs text-muted-foreground">Files generated</p></div>
                </div>
              </div>
            </div>

            <div className="mt-4 rounded-2xl border border-border bg-card p-6">
              <p className="mb-2 text-sm font-semibold">What we adapted</p>
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="replaced">
                  <AccordionTrigger data-testid="report-replaced">Settings we replaced ({(report.replaced || []).length})</AccordionTrigger>
                  <AccordionContent>
                    {(report.replaced || []).length ? (
                      <ul className="space-y-2">
                        {report.replaced.map((r, i) => (
                          <li key={i} className="rounded-xl bg-secondary p-3 text-sm">
                            <p className="font-medium">{r.detail}</p>
                            <p className="mt-1 font-mono text-xs text-muted-foreground">{r.file}</p>
                          </li>
                        ))}
                      </ul>
                    ) : <p className="text-sm text-muted-foreground">Nothing needed replacing.</p>}
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="generated">
                  <AccordionTrigger data-testid="report-generated">Files we generated ({(report.summary?.generated || []).length})</AccordionTrigger>
                  <AccordionContent>
                    <ul className="space-y-1">
                      {(report.summary?.generated || []).map((g, i) => (
                        <li key={i} className="flex items-center gap-2 font-mono text-xs text-muted-foreground"><CheckCircle size={14} weight="fill" className="text-success-foreground" /> {g}</li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="attention">
                  <AccordionTrigger data-testid="report-attention">Needs your attention ({(report.attention || []).length})</AccordionTrigger>
                  <AccordionContent>
                    {(report.attention || []).length ? (
                      <ul className="space-y-2">
                        {report.attention.map((a, i) => (
                          <li key={i} className="flex items-start gap-2 rounded-xl bg-warning/40 p-3 text-sm text-warning-foreground">
                            <Warning size={16} weight="fill" className="mt-0.5 shrink-0" /><div><p className="font-medium">{a.title}</p><p className="text-xs">{a.detail}</p></div>
                          </li>
                        ))}
                      </ul>
                    ) : <p className="text-sm text-muted-foreground">Nothing needs your attention. 🎉</p>}
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="tests">
                  <AccordionTrigger data-testid="report-tests">Test results ({(report.test_checks || []).length})</AccordionTrigger>
                  <AccordionContent>
                    <ul className="space-y-2">
                      {(report.test_checks || []).map((c, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm">
                          {c.ok ? <CheckCircle size={16} weight="fill" className="text-success-foreground" /> : <Warning size={16} weight="fill" className="text-warning-foreground" />}
                          {c.label}
                        </li>
                      ))}
                    </ul>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
